import {
  ModalDialogHeader,
  ModalDialogTitle,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialog,
  ModalDialogContent,
} from '@/components/ModalDialog'
import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import type ConsultationSchedulerViewModel from '../../ConsultationSchedulerViewModel'
import { ButtonGroup } from '@/components/ButtonGroup'
import { ConsolidatedTimeSlotListContainer } from '../ConsolidatedTimeSlotList/ConsolidatedTimeSlotListContainer'

interface ConfirmAvailabilityDialogProps {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Whether the confirm operation is pending.
   */
  isPending?: boolean
  /**
   * Called when the dialog is closed.
   */
  onClose: () => void
  /**
   * Called when availability is confirmed.
   */
  onConfirm: () => void
  /**
   * Content to display in the dialog.
   */
  children: React.ReactNode
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the cancel button.
   */
  cancelTestId?: string
  /**
   * Test ID attribute for the confirm button.
   */
  confirmTestId?: string
}

/**
 * Confirm Availability Dialog
 */
export function ConfirmAvailabilityDialog({
  isOpen,
  isPending,
  onClose,
  onConfirm,
  children,
  testId,
  cancelTestId,
  confirmTestId,
}: ConfirmAvailabilityDialogProps) {
  return (
    <ModalDialog size="lg" isOpen={isOpen} onClose={onClose} testId={testId}>
      <ModalDialogHeader>
        <ModalDialogTitle>Review your availability</ModalDialogTitle>
        <ModalDialogDescription>
          These slots aren't confirmed yet, your Pro will choose one and
          finalize your appointment.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter>
        <ButtonGroup stretch>
          <Button
            variant="outline"
            label="Cancel"
            onPress={onClose}
            isDisabled={isPending}
            testId={cancelTestId}
          />
          <Button
            label="Submit availability"
            onPress={onConfirm}
            isDisabled={isPending}
            isPending={isPending}
            testId={confirmTestId}
          />
        </ButtonGroup>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface ConfirmAvailabilityDialogContainerProps {
  vm: ConsultationSchedulerViewModel
}

/**
 * Confirm Availability Dialog Container
 */
export const ConfirmAvailabilityDialogContainer = observer(
  function ConfirmAvailabilityDialogContainer({
    vm,
  }: ConfirmAvailabilityDialogContainerProps) {
    const testId = 'confirm-availability-dialog'
    return (
      <ConfirmAvailabilityDialog
        isOpen={vm.confirmAvailabilityDialog.showing}
        isPending={vm.submitAvailability.pending}
        onClose={() => vm.confirmAvailabilityDialog.close()}
        onConfirm={vm.submitAvailability}
        testId={testId}
        cancelTestId={`${testId}-cancel-button`}
        confirmTestId={`${testId}-confirm-button`}
      >
        <ConsolidatedTimeSlotListContainer timeRanges={vm.selectedTimeRanges} />
      </ConfirmAvailabilityDialog>
    )
  },
)
